import { b64toBlob } from 'helpers/fileServices';
import { formatStateLabel, formattedDateTimezone } from 'helpers/utils';
import { TAuthConverter, TConvertor, TCreateConverter, TFetchConverter } from '../types/converter.types';
import {
  EIntegrationType,
  EIntegrationTypeDB,
  ESycnPeriodicityDB,
  ESycnPeriodicityUI,
  TConnectEntrataIntegrationDB,
  TConnectEntrataIntegrationUI,
  TConnectYardiIntegrationDB,
  TConnectYardiIntegrationUI,
  TEntrataIntegrationDB,
  TEntrataIntegrationSettingsDB,
  TEntrataIntegrationSettingsUI,
  TEntrataIntegrationUI,
  TUpdateEntrataIntegrationDB,
  TUpdateEntrataIntegrationUI,
  TIntegrationDataExportDB,
  TIntegrationDataExportUI,
  TIntegrationDataUI,
  TIntegrationExecutionResultProps,
  TIntegrationResultProps,
  TIntegrationResultPropsUI,
  TPetChargeSyncEnableValidateDB,
  TPetChargeSyncEnableValidateUI,
  TPropertyForMatchDB,
  TPropertyForMatchUI,
  TPropertySyncStateBulkDB,
  TPropertySyncStateBulkUI,
  TPropertySyncStateDB,
  TPropertySyncStateUI,
  TPUploadSyncUI,
  TUploadIntegrationSettingsDB,
  TUploadIntegrationSettingsUI,
  TResidentsPetChargesUploadDataUI,
  TResmanConnectDB,
  TResmanConnectUI,
  TResmanIntegrationDB,
  TResmanIntegrationUI,
  TResmanPropertiesDB,
  TResmanPropertiesUI,
  TResmanPropertyMatchUI,
  TSycnScheduleDB,
  TSycnScheduleUI,
  TSyncPeriodicityDB,
  TSyncPeriodicityUI,
  TYardiChargeCodesDB,
  TYardiChargeCodesUI,
  TYardiIntegrationDB,
  TYardiIntegrationSettingsDB,
  TYardiIntegrationSettingsUI,
  TYardiIntegrationUI,
  TIntegrationPropertyDB,
  TPropertyMatchDB,
  TPropertyMatchUI,
  TYardiPropertyMatchDB,
  TYardiPropertyMatchUI,
  TYardiPropertySyncStateDB,
  TYardiPropertySyncStateUI,
  TYardiPropertySyncStateBulkDB,
  TYardiPropertySyncStateBulkUI,
  TIntegrationPropertyUI,
  TYardiVersionsDB,
  TYardiVersionsUI,
  TDocumentPushConfigFields,
  ESyncStatusDB,
  ESyncStatusUI,
} from '../types/integration.types';
import { yesNoOptionConverter } from './general.converters';

const documentPushConfigConverter: TConvertor<TDocumentPushConfigFields, TDocumentPushConfigFields> = {
  fromDb: (data) => ({
    documentPushBackConfiguration: data.documentPushBackConfiguration,
    residentAttachmentType: data.residentAttachmentType,
    ...(data.guestAttachmentType ? { guestAttachmentType: data.guestAttachmentType } : {}),
  }),
  toDb: (data) => ({
    documentPushBackConfiguration: data.documentPushBackConfiguration,
    residentAttachmentType: data.residentAttachmentType,
    ...(data.guestAttachmentType ? { guestAttachmentType: data.guestAttachmentType } : {}),
  }),
};

const integrationResultConverter: TFetchConverter<TIntegrationResultPropsUI, TIntegrationResultProps> = {
  fromDb: (data) => {
    return {
      numberOfNewApplicantAnimals: data.numberOfNewApplicantAnimals,
      numberOfFoundMovedOutTenants: data.numberOfFoundMovedOutTenants,
      numberOfFoundMovedOutAnimals: data.numberOfFoundMovedOutAnimals,
      foundMovedOutAnimalsFile: data.foundMovedOutAnimalsFile
        ? b64toBlob(data.foundMovedOutAnimalsFile, 'text/csv')
        : null,
      foundMovedOutTenantsFile: data.foundMovedOutTenantsFile
        ? b64toBlob(data.foundMovedOutTenantsFile, 'text/csv')
        : null,
      applicantsAnimals: data.applicants?.numberOfAnimals,
      applicantsResidencies: data.applicants?.numberOfResidencies,
      applicantAnimalsFile: data.applicantAnimalsFile ? b64toBlob(data.applicantAnimalsFile, 'text/csv') : null,
      applicantsFile: data.applicantsFile ? b64toBlob(data.applicantsFile, 'text/csv') : null,
      movedInResidencies: data.moveInResidencies.numberOfResidencies,
      movedInTotalAnimals: data.moveInResidencies.numberOfAnimals + data.numberOfNewAnimals,
      movedInAnimals: data.moveInResidencies.numberOfAnimals,
      numberOfNewAnimals: data.numberOfNewAnimals,
      movedOutTotalResidencies:
        data.moveOutResidencies.numberOfResidencies + data.terminatingMissingPropertyResidences.numberOfResidencies,
      movedOutResidencies: data.moveOutResidencies.numberOfResidencies,
      movedOutAnimals: data.moveOutResidencies.numberOfAnimals,
      movedOutResidenciesFromMissingProperty: data.terminatingMissingPropertyResidences.numberOfResidencies,
      numberOfMissingProperties: data.terminatingMissingPropertyResidences.numberOfProperties,
      numberOfMatchedProperties: data.numberOfMatchedProperties,
      numberOfSkippedResidencies: data.skippedNotFoundPropertyResidences.numberOfResidencies,
      numberOfSkippedProperties: data.skippedNotFoundPropertyResidences.numberOfProperties,
      numberOfAnimalsSkippedAsDuplicate: data.numberOfAnimalsSkippedAsDuplicate,
      skippedResidenciesCountBecauseOfTenantDuplication: data.skippedResidenciesCountBecauseOfTenantDuplication,
      numberOfResidenciesToMove: data.numberOfResidenciesToMove,
      numberOfApplicantsToMove: data.numberOfApplicantsToMove,
      numberOfSkippedApplicantsProperties: data.skippedNotFoundPropertyApplications?.numberOfProperties || 0,
      numberOfSkippedApplicants: data.skippedNotFoundPropertyResidences?.numberOfApplicants || 0,
      moveInResidenciesFile: data.moveInResidenciesFile ? b64toBlob(data.moveInResidenciesFile, 'text/csv') : null,
      moveInAnimalsFile: data.moveInAnimalsFile ? b64toBlob(data.moveInAnimalsFile, 'text/csv') : null,
      moveOutFile: data.moveOutFile ? b64toBlob(data.moveOutFile, 'text/csv') : null,
      moveResidenciesFile: data.moveResidenciesFile ? b64toBlob(data.moveResidenciesFile, 'text/csv') : null,
      numberOfPropertiesToOnboard: data.numberOfPropertiesToOnboard,
      numberOfResidenciesToTagAsKnown: data.numberOfResidenciesToTagAsKnown,
      numberOfAnimalsToTagAsKnown: data.numberOfAnimalsToTagAsKnown,
    };
  },
};
export const uploadCSVPreviewConverter: TAuthConverter<
  TIntegrationDataUI,
  FormData,
  TIntegrationResultPropsUI,
  TIntegrationResultProps
> = {
  fromDb: (data) => {
    return integrationResultConverter.fromDb(data);
  },
  toDb: (data) => {
    const formData = new FormData();

    formData.append('type', data.type.value);
    formData.append('configuration.shouldConsiderMoveOut', data.shouldConsiderMoveOut);
    if (data.shouldConsiderMissingProperties) {
      formData.append('configuration.shouldConsiderMissingProperties', data.shouldConsiderMissingProperties);
    }
    data.csvFile.forEach((item) => {
      formData.append('csvFile', item);
    });
    return formData;
  },
};

export const uploadTenantsPetChargesImportConverter: TCreateConverter<TResidentsPetChargesUploadDataUI, FormData> = {
  toDb: (data) => {
    const formData = new FormData();
    formData.append('skipMovedOutTenantsAndAnimals', (!data.skipMovedOutTenantsAndAnimals)?.toString());
    formData.append('importType', data.importType.value);
    formData.append('propertyId', data.propertyId);
    formData.append('configuration.shouldConsiderMoveOut', data.shouldConsiderMoveOut);
    if (data.shouldConsiderMissingProperties) {
      formData.append('configuration.shouldConsiderMissingProperties', data.shouldConsiderMissingProperties);
    }
    data.tenantsFile.forEach((item) => {
      formData.append('tenantsFile', item);
    });
    data.petChargesFile.forEach((item) => {
      formData.append('petChargesFile', item);
    });
    return formData;
  },
};

export const uploadTenantsPetChargesCSVPreviewConverter: TAuthConverter<
  TResidentsPetChargesUploadDataUI,
  FormData,
  TIntegrationResultPropsUI,
  TIntegrationResultProps
> = {
  fromDb: (data) => {
    return integrationResultConverter.fromDb(data);
  },
  toDb: (data) => {
    return uploadTenantsPetChargesImportConverter.toDb(data);
  },
};

export const uploadTenantsPetChargesCSVExecuteConverter: TCreateConverter<TResidentsPetChargesUploadDataUI, FormData> =
  {
    toDb: (data) => {
      return uploadTenantsPetChargesImportConverter.toDb(data);
    },
  };
export const uploadCSVExecuteConverter: TAuthConverter<
  TIntegrationDataUI,
  FormData,
  TIntegrationExecutionResultProps,
  TIntegrationExecutionResultProps
> = {
  fromDb: (data) => ({
    matchedPropertiesMoveInResidencies: data.matchedPropertiesMoveInResidencies,
    matchedPropertiesMoveOutResidencies: data.matchedPropertiesMoveOutResidencies,
    terminatingMissingPropertyResidences: data.terminatingMissingPropertyResidences,
  }),
  toDb: (data) => {
    const formData = new FormData();
    formData.append('skipMovedOutTenantsAndAnimals', (!data.skipMovedOutTenantsAndAnimals)?.toString());
    formData.append('type', data.type.value);
    formData.append('configuration.shouldConsiderMoveOut', data.shouldConsiderMoveOut);
    if (data.shouldConsiderMissingProperties) {
      formData.append('configuration.shouldConsiderMissingProperties', data.shouldConsiderMissingProperties);
    }
    data.csvFile.forEach((item) => {
      formData.append('csvFile', item);
    });
    return formData;
  },
};

export const syncPeriodicityUnitConverter: TConvertor<ESycnPeriodicityUI, ESycnPeriodicityDB> = {
  fromDb: (data) => {
    const dataMapper: { [key in ESycnPeriodicityDB]: ESycnPeriodicityUI } = {
      [ESycnPeriodicityDB.DAILY]: ESycnPeriodicityUI.DAILY,
      [ESycnPeriodicityDB.HOURS]: ESycnPeriodicityUI.HOURS,
      // [ESycnPeriodicityDB.MINUTES]: ESycnPeriodicityUI.MINUTES,
      [ESycnPeriodicityDB.MONTHLY]: ESycnPeriodicityUI.MONTHLY,
      [ESycnPeriodicityDB.WEEKLY]: ESycnPeriodicityUI.WEEKLY,
    };
    return dataMapper[data];
  },
  toDb: (data) => {
    const dataMapper: { [key in ESycnPeriodicityUI]: ESycnPeriodicityDB } = {
      [ESycnPeriodicityUI.DAILY]: ESycnPeriodicityDB.DAILY,
      [ESycnPeriodicityUI.HOURS]: ESycnPeriodicityDB.HOURS,
      // [ESycnPeriodicityUI.MINUTES]: ESycnPeriodicityDB.MINUTES,
      [ESycnPeriodicityUI.MONTHLY]: ESycnPeriodicityDB.MONTHLY,
      [ESycnPeriodicityUI.WEEKLY]: ESycnPeriodicityDB.WEEKLY,
    };

    return dataMapper[data];
  },
};

export const integrationSyncStatusConverter: TFetchConverter<ESyncStatusUI, ESyncStatusDB> = {
  fromDb: (data) => {
    const dataMapper: { [key in ESyncStatusDB]: ESyncStatusUI } = {
      [ESyncStatusDB.SUCCESS]: ESyncStatusUI.SUCCESS,
      [ESyncStatusDB.FAILED]: ESyncStatusUI.FAILED,
      [ESyncStatusDB.IN_PROGRESS]: ESyncStatusUI.IN_PROGRESS,
      [ESyncStatusDB.QUEUED]: ESyncStatusUI.QUEUED,
    };
    return dataMapper[data];
  },
};

export const integrationPropertyConvertor: TFetchConverter<TIntegrationPropertyUI, TIntegrationPropertyDB> = {
  fromDb: (data) => {
    return {
      policyId: data.policyId,
      canPlanSync: data.canPlanSync,
      id: data.id,
      name: data.name,
      propertyMatched: data.propertyMatched,
      state: formatStateLabel(data.state),
      unitsCount: data.unitsCount,
      isSyncEnabled: data.applicantsSyncEnabled || data.residentsSyncEnabled,
      applicantsSyncEnabled: data.applicantsSyncEnabled,
      residentsSyncEnabled: data.residentsSyncEnabled,
      guestCardEventPushBackEnabled: yesNoOptionConverter.fromDb(data.guestCardEventPushBackEnabled),
      guestCardAgentName: data.guestCardAgentName ?? null,
      city: data.city,
      address1: data.address1,
      zip: data.zip,
      existingApplicantsLastSyncTime: data.existingApplicantsLastSyncTime
        ? formattedDateTimezone(data.existingApplicantsLastSyncTime, 'MM/DD/YYYY [at] hh:mm A')
        : undefined,
      newApplicantsLastSyncTime: data.newApplicantsLastSyncTime
        ? formattedDateTimezone(data.newApplicantsLastSyncTime, 'MM/DD/YYYY [at] hh:mm A')
        : undefined,
      residentsLastSyncTime: data.residentsLastSyncTime
        ? formattedDateTimezone(data.residentsLastSyncTime, 'MM/DD/YYYY [at] hh:mm A')
        : undefined,
      matchedProperty: data.matchedProperty,
      settingsId: data.settingsId,
      settingsDatabase: data.settingsDatabase,
      organizationId: data.organizationId,
      residentsSyncStatus: data.residentsSyncStatus
        ? integrationSyncStatusConverter.fromDb(data.residentsSyncStatus)
        : null,
      newApplicantsSyncStatus: data.newApplicantsSyncStatus
        ? integrationSyncStatusConverter.fromDb(data.newApplicantsSyncStatus)
        : null,
      existingApplicantsSyncStatus: data.existingApplicantsSyncStatus
        ? integrationSyncStatusConverter.fromDb(data.existingApplicantsSyncStatus)
        : null,
      residentsSyncFailReason: data.residentsSyncFailReason ?? null,
      newApplicantsSyncFailReason: data.newApplicantsSyncFailReason ?? null,
      existingApplicantsSyncFailReason: data.existingApplicantsSyncFailReason ?? null,
      residentsLastSyncDate: data.residentsLastSyncTime ? formattedDateTimezone(data.residentsLastSyncTime) : undefined,
      newApplicantsLastSyncDate: data.newApplicantsLastSyncTime
        ? formattedDateTimezone(data.newApplicantsLastSyncTime)
        : undefined,
      existingApplicantsLastSyncDate: data.existingApplicantsLastSyncTime
        ? formattedDateTimezone(data.existingApplicantsLastSyncTime)
        : undefined,
    };
  },
};
export const yardiSyncPeriodicityTextConvertor: TFetchConverter<string | undefined, TSyncPeriodicityDB> = {
  fromDb(data) {
    return data.unit === ESycnPeriodicityDB.HOURS ? `Every ${data.quantity} hours` : undefined;
    //  data.unit === ESycnPeriodicityDB.MINUTES
    //   ? `Every ${data.quantity} minutes`
    //   :
  },
};

export const yardiSyncPeriodicityConvertor: TFetchConverter<TSyncPeriodicityUI, TSyncPeriodicityDB> = {
  fromDb: (data) => {
    return {
      unit: syncPeriodicityUnitConverter.fromDb(data.unit),
      quantity: data.quantity,
      previewText: yardiSyncPeriodicityTextConvertor.fromDb(data),
    };
  },
};

export const yardiVersionsConverter: TFetchConverter<TYardiVersionsUI, TYardiVersionsDB> = {
  fromDb: (data) => {
    return {
      coreInterfaceVersion: data.coreInterfaceVersion,
      pluginVersion: data.pluginVersion,
      voyagerVersion: data.voyagerVersion,
    };
  },
};
export const yardiIntegrationSettingsConvertor: TFetchConverter<
  TYardiIntegrationSettingsUI,
  TYardiIntegrationSettingsDB
> = {
  fromDb: (data) => {
    return {
      canConnect: data.canConnect,
      settings: data.settings && (data.settings || []).map((setting) => yardiIntegrationInfoConvertor.fromDb(setting)),
    };
  },
};

export const yardiIntegrationInfoConvertor: TFetchConverter<TYardiIntegrationUI, TYardiIntegrationDB> = {
  fromDb: (data) => {
    return {
      id: data.id,
      isTestMode: data.isTestMode,
      database: data.database || '',
      password: '*********',
      username: data.username || '',
      baseUrl: data.baseUrl || '',
      petFeeChargeCodes: data.petFeeChargeCodes?.filter((item) => !!item),
      petRentChargeCodes: data.petRentChargeCodes?.filter((item) => !!item),
      applicantsUpdateSyncPeriodicity: data.applicantsUpdateSyncPeriodicity
        ? yardiSyncPeriodicityConvertor.fromDb(data.applicantsUpdateSyncPeriodicity)
        : null,
      newApplicantsSyncPeriodicity: data.newApplicantsSyncPeriodicity
        ? yardiSyncPeriodicityConvertor.fromDb(data.newApplicantsSyncPeriodicity)
        : null,
      residentSyncPeriodicity: data.residentSyncPeriodicity
        ? yardiSyncPeriodicityConvertor.fromDb(data.residentSyncPeriodicity)
        : null,
      residentDataVersion: data.residentDataVersion ? yardiVersionsConverter.fromDb(data.residentDataVersion) : null,
      ilgGuestCardVersion: data.ilgGuestCardVersion ? yardiVersionsConverter.fromDb(data.ilgGuestCardVersion) : null,
      ...documentPushConfigConverter.fromDb(data),
    };
  },
};

export const yardiIntegrationSyncPeriodicityConvertor: TCreateConverter<TSycnScheduleUI, TSycnScheduleDB> = {
  toDb: (data) => {
    return {
      residentSyncPeriodicity: {
        unit: syncPeriodicityUnitConverter.toDb(data.residents?.label as ESycnPeriodicityUI),
        quantity: Number(data.residentsQuantity?.value) || 1,
      },
      applicantsUpdateSyncPeriodicity: {
        unit: syncPeriodicityUnitConverter.toDb(data.applicantsUpdate?.label as ESycnPeriodicityUI),
        quantity: Number(data.applicantsUpdateQuantity?.value) || 1,
      },
      newApplicantsSyncPeriodicity: {
        unit: syncPeriodicityUnitConverter.toDb(data.newApplicants?.label as ESycnPeriodicityUI),
        quantity: Number(data.newApplicantsQuantity?.value) || 1,
      },
    };
  },
};

export const propertyMatchConverter: TCreateConverter<TPropertyMatchUI, TPropertyMatchDB> = {
  toDb: (data) => {
    return {
      propertyId: data.propertyId,
      linkedPropertyId: data.newPropertyId || data.matchedProperty.value,
      applicantsEnabled: !!data.applicantsEnabled,
      residentsEnabled: !!data.residentsEnabled,
    };
  },
};

// Yardi-only: extends the shared property match converter with the guest card
// events push-back fields. These are sent only when Applicants is enabled.
export const yardiPropertyMatchConverter: TCreateConverter<TYardiPropertyMatchUI, TYardiPropertyMatchDB> = {
  toDb: (data) => {
    const base = propertyMatchConverter.toDb(data);
    if (!data.applicantsEnabled) {
      return base;
    }
    return {
      ...base,
      guestCardEventPushBackEnabled: data.guestCardEventPushBackEnabled === 'Yes',
      guestCardAgentName: data.guestCardEventPushBackEnabled === 'Yes' ? data.guestCardAgentName ?? null : null,
    };
  },
};

export const yardiNotMatchedProperties: TFetchConverter<TPropertyForMatchUI, TPropertyForMatchDB> = {
  fromDb: (data) => {
    return {
      defaultPolicy: data.hasAssignedPolicy,
      label: data.name,
      value: data.id,
      policyId: data.policyId,
    };
  },
};

export const propertySyncStatus: TCreateConverter<TPropertySyncStateUI, TPropertySyncStateDB> = {
  toDb: (data) => {
    return {
      propertyId: data.propertyId,
      applicantsEnabled: data.applicantsEnabled || false,
      residentsEnabled: data.residentsEnabled || false,
    };
  },
};

// Yardi-only: extends the shared sync state converter with the guest card
// events push-back fields. These are sent only when Applicants is enabled.
export const yardiPropertySyncStatus: TCreateConverter<TYardiPropertySyncStateUI, TYardiPropertySyncStateDB> = {
  toDb: (data) => {
    const base = propertySyncStatus.toDb(data);
    if (!data.applicantsEnabled) {
      return base;
    }
    return {
      ...base,
      guestCardEventPushBackEnabled: data.guestCardEventPushBackEnabled === 'Yes',
      guestCardAgentName: data.guestCardEventPushBackEnabled === 'Yes' ? data.guestCardAgentName ?? null : null,
    };
  },
};

export const propertySyncStatusBulk: TCreateConverter<TPropertySyncStateBulkUI, TPropertySyncStateBulkDB> = {
  toDb: (data) => {
    return {
      yardiPropertyIds: data.propertyIds,
      applicantsEnabled: data.applicantsEnabled,
      residentsEnabled: data.residentsEnabled,
    };
  },
};

// Yardi-only bulk: extends the shared bulk sync converter with the guest card
// events push-back fields. These are sent only when Applicants is enabled.
export const yardiPropertySyncStatusBulk: TCreateConverter<
  TYardiPropertySyncStateBulkUI,
  TYardiPropertySyncStateBulkDB
> = {
  toDb: (data) => {
    const base = propertySyncStatusBulk.toDb(data);
    if (!data.applicantsEnabled) {
      return base;
    }
    return {
      ...base,
      guestCardEventPushBackEnabled: data.guestCardEventPushBackEnabled === 'Yes',
      guestCardAgentName: data.guestCardEventPushBackEnabled === 'Yes' ? data.guestCardAgentName ?? null : null,
    };
  },
};

export const entrataPropertySyncStatusBulk: TCreateConverter<TPropertySyncStateBulkUI, TPropertySyncStateBulkDB> = {
  toDb: (data) => {
    return {
      entrataPropertyIds: data.propertyIds,
      applicantsEnabled: data.applicantsEnabled,
      residentsEnabled: data.residentsEnabled,
    };
  },
};

export const resmanIntegrationConverter: TFetchConverter<TResmanIntegrationUI, TResmanIntegrationDB> = {
  fromDb: (data) => {
    return {
      accountId: data.accountId || undefined,
      canConnect: !!data.canConnect,
      id: data.id || undefined,
      syncPeriodicity:
        (data.syncPeriodicity && {
          value: data.syncPeriodicity.unit,
          label: data.syncPeriodicity.unit.charAt(0) + data.syncPeriodicity.unit.slice(1).toLowerCase(),
        }) ||
        undefined,
    };
  },
};

export const resmanIntegrationConnectConverter: TCreateConverter<TResmanConnectUI, TResmanConnectDB> = {
  toDb: (data) => {
    return {
      resManId: data.resManId,
      syncPeriodicity: data.syncPeriodicity && {
        unit: data.syncPeriodicity.value,
        quantity: 1,
      },
      settingsId: data.settingsId,
    };
  },
};

export const resmanPropertyConvertor: TFetchConverter<TResmanPropertiesUI, TResmanPropertiesDB> = {
  fromDb: (data) => {
    return {
      id: data.id,
      canPlanSync: data.canPlanSync,
      name: data.name,
      address1: data.address1,
      city: data.city,
      unitsCount: data.unitCount,
      type: data.type,
      status: data.status,
      isStateSupported: data.isStateSupported,
      residentsLastSyncTime: data.lastSyncDate
        ? formattedDateTimezone(data.lastSyncDate, 'MM/DD/YYYY [at] HH:mm A')
        : undefined,
      policy: data.policy,
      state: data.state.charAt(0) + data.state.slice(1).toLowerCase(),
      isSyncEnabled: data.enabled,
      propertyMatched: data.propertyMatched,
      residentsSyncEnabled: data.enabled,
      matchedProperty: data.matchedProperty,
    };
  },
};
export const resmanPropertyMatchConverter: TCreateConverter<TPropertyMatchUI, TResmanPropertyMatchUI> = {
  toDb: (data) => {
    return {
      propertyId: data.propertyId,
      linkedPropertyId: data.newPropertyId || data.matchedProperty.value,
      residentsEnabled: !!data.residentsEnabled,
    };
  },
};

export const resmanPropertySycnConverter: TCreateConverter<TPropertySyncStateUI, TPropertySyncStateDB> = {
  toDb: (data) => {
    return {
      propertyId: data.propertyId,
      residentsEnabled: !!data.residentsEnabled,
    };
  },
};

export const connectYardiFormConverter: TCreateConverter<TConnectYardiIntegrationUI, TConnectYardiIntegrationDB> = {
  toDb: (data) => {
    return {
      username: data.username,
      password: data.password,
      database: data.database,
      baseUrl: data.baseUrl,
      isTestMode: !!data.isTestMode,
      petRentChargeCodes: data.petRentChargeCodes?.filter((item) => !!item),
      petFeeChargeCodes: data.petFeeChargeCodes?.filter((item) => !!item),
      ...documentPushConfigConverter.toDb(data),
    };
  },
};
export const petChargeStatusSyncEnableValidationConvertor: TFetchConverter<
  TPetChargeSyncEnableValidateUI,
  TPetChargeSyncEnableValidateDB
> = {
  fromDb: ({ chargeCodeInfo, policyInfo }) => {
    let chargeCodesErrorStr = '';
    const isPolicyError = !policyInfo || (!policyInfo?.petFeeChargeAmount && !policyInfo?.petRentChargeAmount);
    if (
      policyInfo?.petFeeChargeAmount &&
      policyInfo?.petRentChargeAmount &&
      !chargeCodeInfo?.petRentChargeCodes.length &&
      !chargeCodeInfo?.petFeeChargeCodes.length
    ) {
      chargeCodesErrorStr = 'Pet Rent and Pet Fee Charge Codes must be provided!';
    } else if (policyInfo?.petRentChargeAmount && !chargeCodeInfo?.petRentChargeCodes.length) {
      chargeCodesErrorStr = 'Pet Rent Charge Code must be provided!';
    } else if (policyInfo?.petFeeChargeAmount && !chargeCodeInfo?.petFeeChargeCodes.length) {
      chargeCodesErrorStr = 'Pet Fee Charge Code must be provided!';
    }
    return {
      canEnable: !isPolicyError && !chargeCodesErrorStr,
      isPolicyError,
      chargeCodesErrorStr: !isPolicyError ? chargeCodesErrorStr : '',
      chargeCodeInfo: {
        petFeeChargeCodes: chargeCodeInfo?.petFeeChargeCodes?.filter((item) => !!item),
        petRentChargeCodes: chargeCodeInfo?.petRentChargeCodes?.filter((item) => !!item),
      },
      policyInfo: {
        petFeeChargeAmount: policyInfo?.petFeeChargeAmount,
        petRentChargeAmount: policyInfo?.petRentChargeAmount,
      },
    };
  },
};

export const integrationDataExportConverter: TCreateConverter<TIntegrationDataExportUI, TIntegrationDataExportDB> = {
  toDb: ({ integrationType, propertyManagementId, propertyId }) => {
    return {
      ...(integrationType === 'Yardi' && { yardiPropertyId: propertyId }),
      ...(integrationType === 'Resman' && { resManPropertyId: propertyId }),
      ...(integrationType === 'Entrata' && { entrataPropertyId: propertyId }),
      propertyManagementId,
    };
  },
};

export const petChargesCodesUpdateConverter: TCreateConverter<TYardiChargeCodesUI, TYardiChargeCodesDB> = {
  toDb: (data) => ({
    petFeeChargeCodes: data.petFeeChargeCodes?.filter((item) => !!item),
    petRentChargeCodes: data.petRentChargeCodes?.filter((item) => !!item),
    ...documentPushConfigConverter.toDb(data),
  }),
};
export const uploadIntegrationSettingsConverter: TFetchConverter<
  TUploadIntegrationSettingsUI,
  TUploadIntegrationSettingsDB
> = {
  fromDb: (data) => {
    return {
      id: data.id,
      canConnect: data.canConnect,
      isConnected: data.isConnected,
      lastUploadDate: data.lastUploadDate ? formattedDateTimezone(data.lastUploadDate) : undefined,
    };
  },
};

export const tpUploadSyncConverter: TCreateConverter<TPUploadSyncUI, FormData> = {
  toDb: (data) => {
    const formData = new FormData();

    formData.append('type', data.type);
    data.zipFile.forEach((item) => {
      formData.append('zipFile', item);
    });
    return formData;
  },
};

export const integrationTypeConverter: TConvertor<EIntegrationType, EIntegrationTypeDB> = {
  fromDb: (data) => {
    const dataMapper: { [key in EIntegrationTypeDB]: EIntegrationType } = {
      [EIntegrationTypeDB.REAL_PAGE]: EIntegrationType.REAL_PAGE,
      [EIntegrationTypeDB.RESMAN]: EIntegrationType.RESMAN,
      [EIntegrationTypeDB.YARDI]: EIntegrationType.YARDI,
      [EIntegrationTypeDB.ENTRATA]: EIntegrationType.ENTRATA,
      [EIntegrationTypeDB.APFOLIO]: EIntegrationType.APFOLIO,
    };
    return dataMapper[data];
  },
  toDb: (data) => {
    const dataMapper: { [key in EIntegrationType]: EIntegrationTypeDB } = {
      [EIntegrationType.REAL_PAGE]: EIntegrationTypeDB.REAL_PAGE,
      [EIntegrationType.RESMAN]: EIntegrationTypeDB.RESMAN,
      [EIntegrationType.YARDI]: EIntegrationTypeDB.YARDI,
      [EIntegrationType.ENTRATA]: EIntegrationTypeDB.ENTRATA,
      [EIntegrationType.APFOLIO]: EIntegrationTypeDB.APFOLIO,
    };

    return dataMapper[data];
  },
};

// ============ ENTRATA CONVERTERS ============

export const entrataIntegrationInfoConverter: TFetchConverter<TEntrataIntegrationUI, TEntrataIntegrationDB> = {
  fromDb: (data) => ({
    id: data.id,
    organizationId: data.organizationId || '',
    residentSyncPeriodicity: data.residentSyncPeriodicity
      ? yardiSyncPeriodicityConvertor.fromDb(data.residentSyncPeriodicity)
      : null,
    newApplicantsSyncPeriodicity: data.newApplicantsSyncPeriodicity
      ? yardiSyncPeriodicityConvertor.fromDb(data.newApplicantsSyncPeriodicity)
      : null,
    applicantsUpdateSyncPeriodicity: data.applicantsUpdateSyncPeriodicity
      ? yardiSyncPeriodicityConvertor.fromDb(data.applicantsUpdateSyncPeriodicity)
      : null,
    petRentChargeCodes: data.petRentChargeCodes?.filter((item) => !!item) || [],
    petFeeChargeCodes: data.petFeeChargeCodes?.filter((item) => !!item) || [],
    ...documentPushConfigConverter.fromDb(data),
  }),
};

export const entrataIntegrationSettingsConverter: TFetchConverter<
  TEntrataIntegrationSettingsUI,
  TEntrataIntegrationSettingsDB
> = {
  fromDb: (data) => {
    return {
      canConnect: data.canConnect,
      settings:
        data.settings && (data.settings || []).map((setting) => entrataIntegrationInfoConverter.fromDb(setting)),
    };
  },
};

export const connectEntrataFormConverter: TCreateConverter<TConnectEntrataIntegrationUI, TConnectEntrataIntegrationDB> =
  {
    toDb: (data) => ({
      organizationId: data.organizationId,
      petRentChargeCodes: data.petRentChargeCodes?.filter((item) => !!item),
      petFeeChargeCodes: data.petFeeChargeCodes?.filter((item) => !!item),
      ...documentPushConfigConverter.toDb(data),
    }),
  };

export const updateEntrataConverter: TCreateConverter<TUpdateEntrataIntegrationUI, TUpdateEntrataIntegrationDB> = {
  toDb: (data) => ({
    entrataSettingsId: data.entrataSettingsId,
    petRentChargeCodes: data.petRentChargeCodes?.filter((item) => !!item),
    petFeeChargeCodes: data.petFeeChargeCodes?.filter((item) => !!item),
    ...documentPushConfigConverter.toDb(data),
  }),
};
